import React, { useState, useEffect } from 'react';
import { Combobox } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon, PlusIcon, BuildingOfficeIcon } from '@heroicons/react/20/solid';
import { getFirestore, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { useAuthContext } from '../../../hooks/useAuthContext';
import AddCompanyModal from '../dashboard/jobs/AddCompanyModal';

const CompanySearch = ({ onCompanySelect, selectedCompanyId, selectedCompanyName }) => {
  const { user } = useAuthContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [allCompanies, setAllCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load all companies on component mount
  useEffect(() => {
    loadAllCompanies();
  }, []);

  // Initialize selected company if provided
  useEffect(() => {
    if (selectedCompanyId && selectedCompanyName) {
      setSelectedCompany({
        id: selectedCompanyId,
        name: selectedCompanyName
      });
      setSearchQuery(selectedCompanyName);
    }
  }, [selectedCompanyId, selectedCompanyName]);

  // Filter companies in real-time as user types
  useEffect(() => {
    console.log('Search query changed:', searchQuery);
    filterCompanies(searchQuery);
  }, [searchQuery, allCompanies]);

  const loadAllCompanies = async () => {
    console.log('🔍 Loading all companies...');
    setIsLoading(true);
    
    try {
      const db = getFirestore();
      console.log('📊 Firestore instance:', db);
      
      const companiesRef = collection(db, 'companies');
      console.log('📁 Companies collection reference:', companiesRef);
      
      // Fetch all companies and sort alphabetically
      const q = query(companiesRef, orderBy('name'));
      console.log('🔍 Executing query...');
      
      const querySnapshot = await getDocs(q);
      console.log('📄 Query snapshot received. Document count:', querySnapshot.size);
      
      const companies = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('🏢 Company data:', { id: doc.id, name: data.name, visible: data.visible });
        
        // Only include visible companies
        if (data.visible !== false) {
          companies.push({
            id: doc.id,
            name: data.name,
            logo: data.logo || null,
            industries: data.industries || [],
            size: data.size || null
          });
        }
      });
      
      console.log('🏢 All visible companies loaded:', companies.length, companies.map(c => c.name));
      
      setAllCompanies(companies);
      setFilteredCompanies(companies.slice(0, 10)); // Show first 10 initially
    } catch (error) {
      console.error('❌ Error loading companies:', error);
      console.error('Error details:', error.message, error.code);
      setAllCompanies([]);
      setFilteredCompanies([]);
    } finally {
      setIsLoading(false);
      console.log('✅ Companies loaded');
    }
  };

  const filterCompanies = (searchTerm) => {
    if (!searchTerm.trim()) {
      // Show first 10 companies when no search term
      const firstTen = allCompanies.slice(0, 10);
      console.log('📋 Showing first 10 companies:', firstTen.map(c => c.name));
      setFilteredCompanies(firstTen);
      return;
    }

    // Filter companies by search term (case-insensitive)
    const filtered = allCompanies.filter(company => 
      company.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    console.log('🔎 Filtered results for "' + searchTerm + '":', filtered.length, filtered.map(c => c.name));
    
    // Limit to 10 results
    const limitedResults = filtered.slice(0, 10);
    setFilteredCompanies(limitedResults);
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    if (company) {
      setSearchQuery(company.name);
      onCompanySelect(company);
    }
  };

  const handleAddNewCompany = (newCompany) => {
    // Add the new company to both all companies and filtered results
    setAllCompanies(prev => [newCompany, ...prev].sort((a, b) => a.name.localeCompare(b.name)));
    setFilteredCompanies(prev => [newCompany, ...prev].sort((a, b) => a.name.localeCompare(b.name)).slice(0, 10));
    
    // Select the new company
    handleCompanySelect(newCompany);
    
    // Close the modal
    setIsAddCompanyModalOpen(false);
  };

  const displayValue = (company) => company?.name || '';

  return (
    <>
      <Combobox value={selectedCompany} onChange={handleCompanySelect}>
        <div className="relative">
          <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white dark:bg-gray-700 text-left border border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-purple-500">
            <Combobox.Input
              className="w-full border-none py-3 pl-4 pr-10 text-lg leading-5 text-gray-900 dark:text-white bg-transparent focus:ring-0 placeholder-gray-400 dark:placeholder-gray-400"
              displayValue={displayValue}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search for your company..."
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              inputMode="search"
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 onboarding-dropdown py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {isLoading && (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700 dark:text-gray-300">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500 mr-2"></div>
                  Loading companies...
                </div>
              </div>
            )}
            
            {!isLoading && filteredCompanies.length === 0 && searchQuery.trim().length > 0 && (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700 dark:text-gray-300">
                No companies found.
              </div>
            )}
            
            {!isLoading && filteredCompanies.map((company) => (
              <Combobox.Option
                key={company.id}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                    active ? 'bg-purple-600 text-white' : 'text-gray-900 dark:text-white'
                  }`
                }
                value={company}
              >
                {({ selected, active }) => (
                  <>
                    <div className="flex items-center">
                      {company.logo ? (
                        <img 
                          src={company.logo} 
                          alt={`${company.name} logo`}
                          className="h-5 w-5 rounded mr-3 object-cover"
                        />
                      ) : (
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                      )}
                      <div className="flex-1">
                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                          {company.name}
                        </span>
                        {company.industries && company.industries.length > 0 && (
                          <span className={`text-xs ${active ? 'text-purple-200' : 'text-gray-500 dark:text-gray-400'}`}>
                            {company.industries.slice(0, 2).join(', ')}
                            {company.size && ` • ${company.size}`}
                          </span>
                        )}
                      </div>
                    </div>
                    {selected ? (
                      <span
                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                          active ? 'text-white' : 'text-purple-600'
                        }`}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Combobox.Option>
            ))}
            
            {/* Add New Company Option */}
            {!isLoading && (
              <div
                className="relative cursor-pointer select-none py-2 pl-10 pr-4 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-gray-600 border-t border-gray-200 dark:border-gray-600"
                onClick={() => setIsAddCompanyModalOpen(true)}
              >
                <div className="flex items-center">
                  <PlusIcon className="h-5 w-5 mr-3" />
                  <span className="font-medium">Add a new company</span>
                </div>
              </div>
            )}
          </Combobox.Options>
        </div>
      </Combobox>

      {/* Add Company Modal */}
      <AddCompanyModal
        isOpen={isAddCompanyModalOpen}
        onClose={() => setIsAddCompanyModalOpen(false)}
        onCompanyAdded={handleAddNewCompany}
        userId={user?.uid}
        zIndex={60}
      />
    </>
  );
};

export default CompanySearch; 